import { Variants } from "framer-motion";

// ================================
// SLIDE VARIANTS
// ================================

/**
 * Slide dari kiri atau kanan
 * @param fromLeft - arah masuk
 * @param duration - opsional, default 2 (detik). Kasih angka lebih kecil buat animasi lebih cepat, mis. 0.6
 * @example
 * <motion.div variants={slideVariants(index % 2 === 0)} ... />          // default (2s)
 * <motion.div variants={slideVariants(index % 2 === 0, 0.6)} ... />     // custom (0.6s)
 */
export const slideVariants = (fromLeft: boolean, duration = 2): Variants => ({
  hidden: { x: fromLeft ? -80 : 80, opacity: 0 },
  visible: { x: 0, opacity: 1, transition: { duration, ease: 'easeOut' } },
});

/**
 * Slide dari bawah ke atas
 * @example
 * <motion.div variants={slideUpVariants()} ... />
 * <motion.div variants={slideUpVariants(0.5)} ... />
 */
export const slideUpVariants = (duration = 2): Variants => ({
  hidden: { y: 60, opacity: 0 },
  visible: { y: 0, opacity: 1, transition: { duration, ease: 'easeOut' } },
});

/**
 * Slide dari atas ke bawah
 * @example
 * <motion.div variants={slideDownVariants()} ... />
 */
export const slideDownVariants = (duration = 2): Variants => ({
  hidden: { y: -60, opacity: 0 },
  visible: { y: 0, opacity: 1, transition: { duration, ease: 'easeOut' } },
});

// ================================
// FADE VARIANTS
// ================================

/**
 * Fade in saja tanpa pergerakan
 * @example
 * <motion.div variants={fadeInVariants()} ... />
 * <motion.div variants={fadeInVariants(0.4)} ... />
 */
export const fadeInVariants = (duration = 2): Variants => ({
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration, ease: 'easeOut' } },
});

/**
 * Fade in + zoom in dari ukuran kecil
 * @example
 * <motion.div variants={fadeZoomInVariants()} ... />
 */
export const fadeZoomInVariants = (duration = 2): Variants => ({
  hidden: { opacity: 0, scale: 0.85 },
  visible: { opacity: 1, scale: 1, transition: { duration, ease: 'easeOut' } },
});

/**
 * Fade in + zoom out dari ukuran besar
 * @example
 * <motion.div variants={fadeZoomOutVariants()} ... />
 */
export const fadeZoomOutVariants = (duration = 2): Variants => ({
  hidden: { opacity: 0, scale: 1.15 },
  visible: { opacity: 1, scale: 1, transition: { duration, ease: 'easeOut' } },
});

// ================================
// SLIDE VARIANTS (eksplisit kiri/kanan)
// ================================

/**
 * Slide masuk dari kiri
 * @example
 * <motion.div variants={slideLeftVariants()} ... />
 */
export const slideLeftVariants = (duration = 2): Variants => ({
  hidden: { x: -80, opacity: 0 },
  visible: { x: 0, opacity: 1, transition: { duration, ease: 'easeOut' } },
});

/**
 * Slide masuk dari kanan
 * @example
 * <motion.div variants={slideRightVariants()} ... />
 */
export const slideRightVariants = (duration = 2): Variants => ({
  hidden: { x: 80, opacity: 0 },
  visible: { x: 0, opacity: 1, transition: { duration, ease: 'easeOut' } },
});

// ================================
// ROTATE VARIANTS
// ================================

/**
 * Fade in + rotate dari kiri (cocok untuk ornamen/icon)
 * @example
 * <motion.div variants={rotateInLeftVariants()} ... />
 */
export const rotateInLeftVariants = (duration = 1.2): Variants => ({
  hidden: { rotate: -15, opacity: 0, scale: 0.9 },
  visible: { rotate: 0, opacity: 1, scale: 1, transition: { duration, ease: 'easeOut' } },
});

/**
 * Fade in + rotate dari kanan
 * @example
 * <motion.div variants={rotateInRightVariants()} ... />
 */
export const rotateInRightVariants = (duration = 1.2): Variants => ({
  hidden: { rotate: 15, opacity: 0, scale: 0.9 },
  visible: { rotate: 0, opacity: 1, scale: 1, transition: { duration, ease: 'easeOut' } },
});

// ================================
// FLIP VARIANTS (3D)
// ================================

/**
 * Flip 3D pada sumbu Y (kiri-kanan) — butuh parent dengan style="perspective: 1000px"
 * @example
 * <motion.div variants={flipXVariants()} ... />
 */
export const flipXVariants = (duration = 1.2): Variants => ({
  hidden: { rotateY: 90, opacity: 0 },
  visible: { rotateY: 0, opacity: 1, transition: { duration, ease: 'easeOut' } },
});

/**
 * Flip 3D pada sumbu X (atas-bawah) — butuh parent dengan style="perspective: 1000px"
 * @example
 * <motion.div variants={flipYVariants()} ... />
 */
export const flipYVariants = (duration = 1.2): Variants => ({
  hidden: { rotateX: 90, opacity: 0 },
  visible: { rotateX: 0, opacity: 1, transition: { duration, ease: 'easeOut' } },
});

// ================================
// BOUNCE / SPRING VARIANTS
// ================================

/**
 * Slide up dengan efek bounce/spring (lebih hidup dibanding easeOut biasa)
 * @param stiffness - default 120, makin besar makin cepat & "kaku"
 * @param damping - default 10, makin besar makin cepat redam (nggak terlalu mantul)
 * @example
 * <motion.div variants={bounceUpVariants()} ... />
 * <motion.div variants={bounceUpVariants(220, 16)} ... /> // lebih cepat & kurang mantul
 */
export const bounceUpVariants = (stiffness = 120, damping = 10): Variants => ({
  hidden: { y: 80, opacity: 0, scale: 0.8 },
  visible: {
    y: 0,
    opacity: 1,
    scale: 1,
    transition: { type: 'spring', stiffness, damping },
  },
});

/**
 * Pop in dengan efek bounce/spring — bagus untuk badge, icon, tombol
 * @example
 * <motion.div variants={popInVariants()} ... />
 * <motion.div variants={popInVariants(320, 18)} ... /> // lebih cepat
 */
export const popInVariants = (stiffness = 200, damping = 12): Variants => ({
  hidden: { scale: 0, opacity: 0 },
  visible: {
    scale: 1,
    opacity: 1,
    transition: { type: 'spring', stiffness, damping },
  },
});

// ================================
// BLUR VARIANTS
// ================================

/**
 * Fade in dengan efek blur ke fokus — kesan elegan/cinematic
 * @example
 * <motion.div variants={blurInVariants()} ... />
 */
export const blurInVariants = (duration = 1.5): Variants => ({
  hidden: { opacity: 0, filter: 'blur(10px)' },
  visible: { opacity: 1, filter: 'blur(0px)', transition: { duration, ease: 'easeOut' } },
});

/**
 * Slide up + blur ke fokus — gabungan slideUp dan blurIn
 * @example
 * <motion.div variants={blurSlideUpVariants()} ... />
 */
export const blurSlideUpVariants = (duration = 1.5): Variants => ({
  hidden: { y: 40, opacity: 0, filter: 'blur(8px)' },
  visible: { y: 0, opacity: 1, filter: 'blur(0px)', transition: { duration, ease: 'easeOut' } },
});

// ================================
// CLIP-PATH VARIANTS (reveal effect)
// ================================

/**
 * Reveal dari kiri ke kanan pakai clip-path — cocok untuk garis/divider/judul section
 * @example
 * <motion.div variants={clipRevealVariants()} ... />
 */
export const clipRevealVariants = (duration = 1.2): Variants => ({
  hidden: { clipPath: 'inset(0 100% 0 0)', opacity: 1 },
  visible: { clipPath: 'inset(0 0% 0 0)', transition: { duration, ease: 'easeOut' } },
});

// ================================
// STAGGER VARIANTS TAMBAHAN
// ================================

/**
 * Item child untuk stagger — zoom in per item
 * Harus dipakai bersama staggerContainerVariants di parent
 * @example
 * <motion.div key={i} variants={staggerZoomItemVariants()}>
 */
export const staggerZoomItemVariants = (duration = 1.5): Variants => ({
  hidden: { scale: 0.7, opacity: 0 },
  visible: { scale: 1, opacity: 1, transition: { duration, ease: 'easeOut' } },
});

/**
 * Item child untuk stagger — rotate + fade per item, cocok untuk gallery/icon grid
 * Harus dipakai bersama staggerContainerVariants di parent
 * @example
 * <motion.div key={i} variants={staggerRotateItemVariants()}>
 */
export const staggerRotateItemVariants = (duration = 1.2): Variants => ({
  hidden: { rotate: -10, scale: 0.8, opacity: 0 },
  visible: { rotate: 0, scale: 1, opacity: 1, transition: { duration, ease: 'easeOut' } },
});

// ================================
// STAGGER VARIANTS (untuk list/grid)
// ================================

/**
 * Container untuk stagger — taruh di wrapper list
 * @param staggerChildren - jeda antar-anak, default 0.15 detik
 * @example
 * <motion.div variants={staggerContainerVariants()} ... >
 *   {items.map((item, i) => (
 *     <motion.div key={i} variants={staggerItemVariants()}>...</motion.div>
 *   ))}
 * </motion.div>
 */
export const staggerContainerVariants = (staggerChildren = 0.15): Variants => ({
  hidden: {},
  visible: {
    transition: { staggerChildren },
  },
});

/**
 * Item child untuk stagger — slide up per item
 * Harus dipakai bersama staggerContainerVariants di parent
 * @example
 * <motion.div variants={staggerItemVariants()}>
 */
export const staggerItemVariants = (duration = 2): Variants => ({
  hidden: { y: 40, opacity: 0 },
  visible: { y: 0, opacity: 1, transition: { duration, ease: 'easeOut' } },
});

/**
 * Item child untuk stagger — slide kiri kanan bergantian
 * Harus dipakai bersama staggerContainerVariants di parent
 * @example
 * <motion.div key={i} variants={staggerSlideItemVariants(i)}>
 * <motion.div key={i} variants={staggerSlideItemVariants(i, 0.6)}>
 */
export const staggerSlideItemVariants = (index: number, duration = 2): Variants => ({
  hidden: { x: index % 2 === 0 ? -80 : 80, opacity: 0 },
  visible: { x: 0, opacity: 1, transition: { duration, ease: 'easeOut' } },
});

// ================================
// GRID / CARD SWITCH VARIANTS
// ================================

/**
 * Container grid — dipakai bareng AnimatePresence saat kontennya
 * berganti (mis. ganti kategori). Anak-anaknya (cardVariants) akan
 * muncul berurutan lewat staggerChildren.
 * @example
 * <motion.div variants={gridVariants()} initial="hidden" animate="visible" exit="exit">
 */
export const gridVariants = (staggerChildren = 0.15): Variants => ({
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren },
  },
  exit: {
    opacity: 0,
    transition: { duration: 0.15 },
  },
});

/**
 * Item child untuk gridVariants — fade + slide-up ringan per kartu.
 * @example
 * <motion.div variants={cardVariants()}>...</motion.div>
 */
export const cardVariants = (duration = 0.35): Variants => ({
  hidden: { opacity: 0, y: 16, scale: 0.98 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration, ease: [0.22, 1, 0.36, 1] },
  },
  exit: {
    opacity: 0,
    y: -8,
    transition: { duration: 0.15 },
  },
});

// ================================
// TAB / UI SWITCH VARIANTS
// ================================

/**
 * Transisi masuk/keluar untuk konten yang berganti karena klik
 * (tab, accordion panel, toggle). Geser dikit + fade, cepat.
 * @example
 * <motion.div variants={tabSwitchVariants()} initial="hidden" animate="visible" exit="exit">
 */
export const tabSwitchVariants = (duration = 0.2): Variants => ({
  hidden: { opacity: 0, y: 8 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration, ease: 'easeOut' },
  },
  exit: {
    opacity: 0,
    y: -4,
    transition: { duration: duration * 0.6, ease: 'easeIn' },
  },
});

/**
 * Helper untuk skip animasi saat isEditMode
 * @example
 * <motion.div variants={fadeInVariants()} {...motionEditProps(isEditMode)} viewport={{ once: true, amount: 0.2 }} />
 */
export const motionEditProps = (isEditMode: boolean) => ({
  initial: isEditMode ? false : "hidden" as const,
  whileInView: isEditMode ? undefined : "visible" as const,
});